import React from 'react';
import { makeStyles, Typography, Grid, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import InputProps from '../InputProps';
import { generateProperties } from '../helpers';
import colors from '../../../theme/colors';

interface NumberPickerProps extends InputProps<number> {
  /** Minimum value the input can have */
  minValue?: number;
  /** Maximum value the input can have */
  maxValue?: number;
  /** How much to add/substract for each button click */
  valueJump?: number;
  showMaxOverflow?: boolean;
  showMinOverflow?: boolean;
}

type DisabledType = {
  disabled: boolean;
};

const useStyles = makeStyles({
  container: ({ disabled }: DisabledType) => ({
    height: 32,
    borderRadius: 16,
    border: `1px solid ${disabled ? colors.silver : colors.bright_turquoise}`,
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  numberContainer: {
    height: 32,
    minWidth: 32,
  },
  numberLabel: ({ disabled }: DisabledType) => ({
    color: disabled ? colors.abbey : colors.madison,
  }),
  buttonContainer: ({ disabled }: DisabledType) => ({
    width: 32,
    height: 32,
    color: disabled ? colors.abbey : colors.madison,
  }),
});

const NumberPicker = ({
  inputId,
  inputName,
  onChange,
  defaultValue = 0,
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  dataSource,
  minValue,
  maxValue,
  valueJump = 1,
  showMaxOverflow = false,
  showMinOverflow = false,
}: NumberPickerProps): JSX.Element => {
  const formInputProps = generateProperties<number>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });
  const classes = useStyles({ disabled: isDisabled });

  function clamp(value: number): number {
    let parsedValue = value;
    if (typeof minValue !== 'undefined') {
      parsedValue = parsedValue < minValue ? minValue : parsedValue;
    }
    if (typeof maxValue !== 'undefined') {
      parsedValue = parsedValue > maxValue ? maxValue : parsedValue;
    }
    return parsedValue;
  }

  function decorate(value: number): string {
    if (
      typeof minValue !== 'undefined' &&
      showMinOverflow &&
      value <= minValue
    ) {
      return `<${value}`;
    }
    if (
      typeof maxValue !== 'undefined' &&
      showMaxOverflow &&
      value >= maxValue
    ) {
      return `${value}+`;
    }
    return value.toString();
  }

  function onValueChanged(newValue: number): void {
    formInputProps.onChange(clamp(newValue));
  }

  return (
    <div className={classes.container}>
      <IconButton
        id={`${inputId}-minus`}
        data-testid="minus-button"
        className={classes.buttonContainer}
        disableRipple
        disabled={isDisabled}
        onClick={(): void => onValueChanged(formInputProps.value - valueJump)}
      >
        <RemoveIcon />
      </IconButton>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={classes.numberContainer}
      >
        <Typography
          data-testid="content"
          variant="body2"
          className={classes.numberLabel}
        >
          {decorate(clamp(formInputProps.value))}
        </Typography>
      </Grid>
      <IconButton
        id={`${inputId}-plus`}
        data-testid="plus-button"
        className={classes.buttonContainer}
        disableRipple
        disabled={isDisabled}
        onClick={(): void => onValueChanged(formInputProps.value + valueJump)}
      >
        <AddIcon />
      </IconButton>
    </div>
  );
};

export default NumberPicker;
